import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNoticePreferences } from '../../hooks/useNoticePreferences';

const PALETTE = {
  bg: '#f9f7f4',
  bgAlt: '#faf7f3',
  border: '#e8d9c4',
  borderStrong: '#c4a880',
  text: '#2c1a0e',
  body: '#3d2b1f',
  muted: '#9b8a7a',
  accent: '#9b6f4b',
  toggleOff: '#d4c4b0',
};

// Renommé depuis CookieConsent → le nom 'cookie/consent' était filtré par
// la plupart des AdBlocks (uBlock Origin, AdGuard, Brave Shields), ce qui
// faisait planter le chargement du chunk en production pour les users avec
// un bloqueur agressif. Path + nom neutres maintenant pour ne pas matcher
// les listes de filtres standard.
const PreferencesNotice: React.FC = () => {
  const navigate = useNavigate();
  const {
    consent,
    hasConsent,
    showSettings,
    acceptAll,
    refuseAll,
    saveCustom,
    openSettings,
    closeSettings,
  } = useNoticePreferences();

  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);

  useEffect(() => {
    if (showSettings) {
      setAnalyticsOn(consent?.analytics ?? false);
      setMarketingOn(consent?.marketing ?? false);
    }
  }, [showSettings, consent]);

  // Allow any component (e.g. footer link) to reopen the settings panel
  // by dispatching `window.dispatchEvent(new Event('glowp:open-cookie-settings'))`.
  useEffect(() => {
    const handler = () => openSettings();
    window.addEventListener('glowp:open-cookie-settings', handler);
    return () =>
      window.removeEventListener('glowp:open-cookie-settings', handler);
  }, [openSettings]);

  const showBanner = !hasConsent && !showSettings;

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            key="banner"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed bottom-0 left-0 right-0 z-[60] px-4 py-4 sm:px-6 sm:py-5"
            style={{
              backgroundColor: PALETTE.bg,
              borderTop: `1px solid ${PALETTE.border}`,
              boxShadow: '0 -4px 24px rgba(44, 26, 14, 0.08)',
              // Dégage la barre home iPhone sous les boutons de consentement.
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            }}
            role="dialog"
            aria-labelledby="cookie-banner-title"
          >
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  style={{ color: PALETTE.accent }}
                />
                <div>
                  <h2
                    id="cookie-banner-title"
                    className="font-semibold text-sm mb-1"
                    style={{ color: PALETTE.text }}
                  >
                    Tes cookies, ton choix
                  </h2>
                  <p
                    className="text-xs leading-relaxed"
                    style={{ color: PALETTE.body }}
                  >
                    On utilise des cookies essentiels au fonctionnement de
                    Glowp, et — si tu acceptes — des cookies de mesure
                    d'audience et marketing pour mieux comprendre comment tu
                    utilises l'app.{' '}
                    <button
                      onClick={() => navigate('/cgu')}
                      className="underline"
                      style={{ color: PALETTE.accent }}
                    >
                      En savoir plus
                    </button>
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:flex-shrink-0">
                <button
                  onClick={refuseAll}
                  className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors"
                  style={{
                    color: PALETTE.accent,
                    backgroundColor: 'transparent',
                    border: `1px solid ${PALETTE.borderStrong}`,
                  }}
                >
                  Tout refuser
                </button>
                <button
                  onClick={openSettings}
                  className="px-4 py-2 text-sm font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors"
                  style={{
                    color: PALETTE.accent,
                    backgroundColor: 'transparent',
                    border: `1px solid ${PALETTE.borderStrong}`,
                  }}
                >
                  <Settings className="w-3.5 h-3.5" />
                  Personnaliser
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 text-sm font-semibold rounded-lg text-white transition-opacity hover:opacity-90"
                  style={{
                    background: 'linear-gradient(135deg, #C8A882, #B89968)',
                  }}
                >
                  Tout accepter
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSettings && (
          <motion.div
            key="settings-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6"
            style={{ backgroundColor: 'rgba(44, 26, 14, 0.5)' }}
            onClick={closeSettings}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col max-h-[90vh]"
              style={{ backgroundColor: PALETTE.bg }}
              role="dialog"
              aria-labelledby="cookie-settings-title"
            >
              <div
                className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0"
                style={{ borderColor: PALETTE.border }}
              >
                <h2
                  id="cookie-settings-title"
                  className="font-bold text-lg"
                  style={{
                    color: PALETTE.text,
                    fontFamily: 'Georgia, serif',
                  }}
                >
                  Préférences de cookies
                </h2>
                <button
                  onClick={closeSettings}
                  className="p-1.5 rounded-lg transition-colors hover:bg-black/5"
                  aria-label="Fermer"
                >
                  <X className="w-5 h-5" style={{ color: PALETTE.muted }} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-5 overflow-y-auto">
                <CookieCategory
                  title="Cookies essentiels"
                  description="Nécessaires au fonctionnement de la plateforme (session, authentification, panier). Ne peuvent pas être désactivés."
                  enabled={true}
                  disabled={true}
                  onToggle={() => {}}
                />
                <CookieCategory
                  title="Mesure d'audience"
                  description="Nous aident à comprendre l'utilisation anonymisée de l'app pour l'améliorer (analytics, données agrégées)."
                  enabled={analyticsOn}
                  disabled={false}
                  onToggle={() => setAnalyticsOn(v => !v)}
                />
                <CookieCategory
                  title="Marketing"
                  description="Permettent de personnaliser les communications marketing et de mesurer l'efficacité de nos campagnes (newsletter, retargeting)."
                  enabled={marketingOn}
                  disabled={false}
                  onToggle={() => setMarketingOn(v => !v)}
                />

                <p className="text-xs" style={{ color: PALETTE.muted }}>
                  Tu peux modifier ces choix à tout moment depuis le footer
                  (lien "Cookies"). Notre{' '}
                  <button
                    onClick={() => {
                      closeSettings();
                      navigate('/cgu');
                    }}
                    className="underline"
                    style={{ color: PALETTE.accent }}
                  >
                    Politique de confidentialité
                  </button>{' '}
                  détaille tous les traitements.
                </p>
              </div>

              <div
                className="px-6 py-4 border-t flex flex-col-reverse sm:flex-row sm:justify-end gap-2 flex-shrink-0"
                style={{
                  borderColor: PALETTE.border,
                  backgroundColor: PALETTE.bgAlt,
                }}
              >
                <button
                  onClick={refuseAll}
                  className="px-4 py-2 text-sm font-semibold rounded-lg transition-colors"
                  style={{
                    color: PALETTE.accent,
                    backgroundColor: 'transparent',
                    border: `1px solid ${PALETTE.borderStrong}`,
                  }}
                >
                  Tout refuser
                </button>
                <button
                  onClick={() => saveCustom(analyticsOn, marketingOn)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg text-white transition-opacity hover:opacity-90"
                  style={{
                    background: 'linear-gradient(135deg, #C8A882, #B89968)',
                  }}
                >
                  Enregistrer mes choix
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

interface CookieCategoryProps {
  title: string;
  description: string;
  enabled: boolean;
  disabled: boolean;
  onToggle: () => void;
}

const CookieCategory: React.FC<CookieCategoryProps> = ({
  title,
  description,
  enabled,
  disabled,
  onToggle,
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1">
      <p className="font-semibold text-sm mb-1" style={{ color: PALETTE.text }}>
        {title}
      </p>
      <p className="text-xs leading-relaxed" style={{ color: PALETTE.body }}>
        {description}
      </p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={onToggle}
      disabled={disabled}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 mt-0.5"
      style={{
        backgroundColor: enabled ? PALETTE.accent : PALETTE.toggleOff,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span
        className="inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform"
        style={{
          transform: enabled ? 'translateX(24px)' : 'translateX(4px)',
        }}
      />
    </button>
  </div>
);

export default PreferencesNotice;
